import { ArrowRight, Sparkles } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/2928342/pexels-photo-2928342.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";
const LOGO = "https://customer-assets.emergentagent.com/job_b4nk-studio/artifacts/ob8a8fa3_logo.jpeg";

const goTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-screen flex items-end overflow-hidden grain"
        >
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={HERO_BG}
                    alt="Tattoo artist at work"
                    className="w-full h-full object-cover grayscale contrast-125 brightness-50"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            </div>

            {/* Grid texture overlay */}
            <div className="absolute inset-0 grid-lines-bg opacity-40 pointer-events-none" />

            {/* Floating big stencil text */}
            <div className="absolute right-[-2%] top-[12%] hidden lg:block opacity-[0.07] pointer-events-none">
                <span className="font-display text-[18rem] leading-none stencil-stroke">B4NK</span>
            </div>

            {/* Ink drip element */}
            <div className="absolute top-0 left-[18%] w-[3px] h-32 bg-[#7CFC00] ink-drip opacity-70 hidden md:block" />
            <div className="absolute top-0 left-[18%] w-3 h-3 bg-[#7CFC00] rounded-full mt-32 ink-drip glow-neon hidden md:block" />

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 pt-32 w-full">
                <div className="max-w-4xl fade-up">
                    <div className="flex items-center gap-3 mb-6" data-testid="hero-overline">
                        <div className="h-px w-10 bg-[#7CFC00]" />
                        <span className="text-[#7CFC00] uppercase tracking-[0.3em] text-xs font-semibold">
                            Dallas · Plano · Custom Ink
                        </span>
                    </div>

                    <h1
                        data-testid="hero-title"
                        className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] leading-[0.9] tracking-tight text-white"
                    >
                        TATZ<span className="text-[#7CFC00] text-glow">BY</span>
                        <br />
                        B4<span className="text-[#7CFC00] text-glow">NK</span>
                    </h1>

                    <p
                        data-testid="hero-tagline"
                        className="mt-8 text-xl md:text-2xl text-zinc-200 max-w-2xl font-light"
                    >
                        Custom Tattoos.{" "}
                        <span className="font-script text-[#7CFC00] text-3xl md:text-4xl">Clean Work.</span>{" "}
                        Dallas Detail.
                    </p>

                    <p className="mt-4 text-zinc-400 max-w-xl text-base md:text-lg">
                        Walk in with an idea. Walk out with a piece you'll keep flexing for life. Built by Shawn,
                        booked solid for a reason.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <button
                            data-testid="hero-book-btn"
                            onClick={() => goTo("booking")}
                            className="group bg-[#7CFC00] text-black font-bold uppercase tracking-widest text-sm md:text-base px-8 py-4 flex items-center gap-3 hover:bg-[#65E000] transition-all glow-neon hover:translate-y-[-2px]"
                        >
                            Book Now
                            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            data-testid="hero-view-work-btn"
                            onClick={() => goTo("portfolio")}
                            className="bg-transparent text-white border border-white/30 uppercase tracking-widest text-sm md:text-base font-bold px-8 py-4 hover:border-[#7CFC00] hover:text-[#7CFC00] transition-colors flex items-center gap-3"
                        >
                            <Sparkles size={18} />
                            View Work
                        </button>
                    </div>

                    {/* Stat strip */}
                    <div className="mt-16 grid grid-cols-3 gap-4 md:gap-10 max-w-2xl border-t border-white/10 pt-8">
                        {[
                            { n: "500+", l: "Pieces Inked" },
                            { n: "5★", l: "Client Rating" },
                            { n: "8 Yrs", l: "Behind the Gun" },
                        ].map((s, i) => (
                            <div key={i} data-testid={`hero-stat-${i}`}>
                                <div className="font-display text-3xl md:text-5xl text-white">{s.n}</div>
                                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Floating logo bottom right */}
            <div className="absolute bottom-10 right-6 md:right-10 z-10 hidden md:block">
                <div className="relative">
                    <img
                        src={LOGO}
                        alt="TATZBYB4NK Logo"
                        className="w-28 h-28 lg:w-40 lg:h-40 object-cover border-2 border-[#7CFC00]/60 pulse-glow"
                    />
                    <div className="absolute -top-3 -left-3 bg-[#7CFC00] text-black text-[10px] uppercase tracking-widest font-bold px-2 py-1">
                        Est. 2017
                    </div>
                </div>
            </div>
        </section>
    );
};
